// Apolo Planning Mode — parse an agent plan into ordered TODOs. PURE.
//
// The Architect/plan format uses `## Plan` with bullet/numbered steps (see
// components/code/PlanCard.jsx). We turn those steps into the structured todos the
// PlanState machine + TodoDropdown render. Falls back to top-level bullets if there's
// no `## Plan` section, so a plain checklist plan still yields todos.

function extractSection(plan, header) {
  if (typeof plan !== 'string') return null;
  const lines = plan.split('\n');
  const headRe = new RegExp(`^##\\s*${header}\\s*$`, 'i');
  const start = lines.findIndex((l) => headRe.test(l.trim()));
  if (start === -1) return null;
  const body = [];
  for (let i = start + 1; i < lines.length; i += 1) {
    if (/^##\s/.test(lines[i].trim())) break; // next section
    body.push(lines[i]);
  }
  return body.join('\n').trim();
}

function bullets(section) {
  if (!section) return [];
  return section
    .split('\n')
    .map((l) => l.trim())
    // A real bullet needs a SPACE after the marker ("- x" / "* x" / "1. x").
    // Without it, "**Plan**" (bold) and "---" (table separator) were wrongly
    // treated as bullets — a bold title then shadowed the table parser.
    .filter((l) => /^[-*]\s/.test(l) || /^\d+\.\s/.test(l))
    .map((l) => l.replace(/^[-*]\s+\[[ xX]\]\s*/, '').replace(/^[-*]\s+/, '').replace(/^\d+\.\s+/, '').trim())
    .filter(Boolean);
}

// Markdown table → step actions. Models routinely narrate a plan as a TABLE
// (`| # | Ação | Ferramenta | Critério |`) instead of a list. Find the "action"
// column from the header (else the column after a leading #/nº column, else the
// first), skip the `| --- |` separator row, and take that cell per data row.
function tableSteps(text) {
  if (typeof text !== 'string') return [];
  const rows = text
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => l.startsWith('|') && l.endsWith('|'))
    .map((l) => l.slice(1, -1).split('|').map((c) => c.trim()));
  if (rows.length < 2) return [];
  const header = rows[0].map((h) => h.toLowerCase());
  let idx = header.findIndex((h) => /a[çc][aã]o|action|passo|step|tarefa|task|descri/.test(h));
  if (idx === -1) idx = /^#$|^n[ºo]\.?$|num/.test(header[0] || '') ? 1 : 0;
  const isSep = (cells) => cells.every((c) => /^:?-{2,}:?$/.test(c) || c === '');
  return rows
    .slice(1)
    .filter((cells) => !isSep(cells))
    .map((cells) => (cells[idx] || cells[cells.length - 1] || '').replace(/\*\*/g, '').replace(/`/g, '').trim())
    .filter(Boolean);
}

/**
 * @param {string} planText
 * @returns {{title:string, todos:Array<{id:string,text:string,status:'pending'}>}}
 */
export function parsePlanTodos(planText) {
  if (typeof planText !== 'string' || !planText.trim()) return { title: '', todos: [] };
  // Title: a leading `# ` heading, or `## Plan`'s presence, else first non-empty line.
  const h1 = planText.match(/^#\s+(.+)$/m);
  const firstLine = planText.split('\n').map((l) => l.trim()).find(Boolean) || '';
  const title = (h1 ? h1[1] : firstLine).slice(0, 120);

  let steps = bullets(extractSection(planText, 'Plan'));
  if (steps.length === 0) steps = bullets(planText); // fallback: any bullets in the doc
  if (steps.length === 0) steps = tableSteps(planText); // fallback: a markdown table plan
  const todos = steps.slice(0, 50).map((text, i) => ({ id: `t${i + 1}`, text, status: 'pending' }));
  return { title, todos };
}

/**
 * Build the structured pending-plan ({summary, steps, questions}) the
 * PlanProposalCard renders, FROM the model's prose/table when it narrated the
 * plan instead of calling present_plan. Returns null when there's no plan text.
 * Prose carries no clarifying questions, so `questions` is always [].
 * @param {string} planText
 * @returns {{summary:string, steps:Array<{n:number,action:string}>, questions:Array}|null}
 */
export function planFromProse(planText) {
  const parsed = parsePlanTodos(planText);
  if (parsed.todos.length) {
    return {
      summary: parsed.title,
      steps: parsed.todos.map((t, i) => ({ n: i + 1, action: t.text })),
      questions: [],
    };
  }
  const txt = typeof planText === 'string' ? planText.trim() : '';
  if (txt) return { summary: txt.slice(0, 2000), steps: [], questions: [] };
  return null;
}
